import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ScrollToTop } from '../components/ScrollToTop';
import { PortalHeader, PortalLoading, getEquipmentImage, formatMaintenanceDate } from '../components/PortalComponents';
import { EquipmentService } from '../services/mockServices';
import { WHATSAPP_MANKLAR_URL } from '../lib/contacto';
import { 
  Calendar, Shield, MapPin, Wrench, Info,
  CheckCircle, AlertTriangle, Clock, Droplets
} from 'lucide-react';

function StatusBadge({ status }) {
  const config = {
    active: { label: 'Activo', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', dot: 'bg-emerald-400' },
    maintenance: { label: 'En revisión', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', dot: 'bg-orange-400' },
    inactive: { label: 'Inactivo', color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', dot: 'bg-zinc-400' }
  };
  const c = config[status] || config.active;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${c.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot} animate-pulse`} />
      {c.label}
    </span>
  );
}

function EquipmentHero({ equipment }) {
  const image = getEquipmentImage(equipment.model);
  const maintenance = formatMaintenanceDate(equipment.nextMaintenance);

  return (
    <div className="mb-8 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-sky-500/10 via-sky-500/5 to-transparent border border-sky-500/20 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/10 rounded-full blur-[100px]" />
      
      <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
        {/* Equipment Image */}
        <div className="w-40 h-40 sm:w-48 sm:h-48 flex-shrink-0">
          <img 
            src={image} 
            alt={equipment.name}
            className="w-full h-full object-contain drop-shadow-[0_0_30px_rgba(56,189,248,0.2)]"
          />
        </div>
        
        {/* Equipment Info */}
        <div className="flex-1 text-center md:text-left">
          <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
            <h2 className="text-2xl sm:text-3xl font-light text-white font-['Outfit']">{equipment.name}</h2>
            <StatusBadge status={equipment.status} />
          </div>
          <p className="text-zinc-400 font-['Manrope'] mb-4">Agua pura ilimitada para tu hogar</p>
          
          {/* Quick Stats */}
          <div className="flex flex-wrap justify-center md:justify-start gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.05]">
              <Shield className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-zinc-300 font-['Manrope']">Garantía Activa</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
              maintenance.status === 'overdue' ? 'bg-red-500/10' : 'bg-white/[0.05]'
            }`}>
              <Calendar className={`w-4 h-4 ${maintenance.status === 'overdue' ? 'text-red-400' : 'text-sky-400'}`} />
              <span className={`text-xs font-['Manrope'] ${maintenance.status === 'overdue' ? 'text-red-300' : 'text-zinc-300'}`}>
                {maintenance.text}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EquipmentDetails({ equipment }) {
  const maintenance = formatMaintenanceDate(equipment.nextMaintenance);

  return (
    <div className="space-y-4">
      {/* Serial & Location */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-['Manrope']">Número de Serie</p>
          <p className="text-sm font-mono text-white">{equipment.serialNumber}</p>
        </div>
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-['Manrope']">Ubicación</p>
          <p className="text-sm text-white font-['Manrope']">{equipment.location}</p>
        </div>
      </div>

      {/* Monthly Price */}
      <div className="p-5 rounded-xl bg-gradient-to-r from-white/[0.03] to-transparent border border-white/10">
        <div className="flex items-center justify-between">
          <span className="text-sm text-zinc-400 font-['Manrope']">Cuota mensual</span>
          <div className="text-right">
            <span className="text-2xl font-light text-white font-['Outfit']">{equipment.monthlyPrice?.toFixed(2)}€</span>
            <span className="text-xs text-zinc-500 ml-2">IVA incluido</span>
          </div>
        </div>
      </div>

      {/* Maintenance Schedule */}
      <div className={`p-4 rounded-xl border ${
        maintenance.status === 'overdue' 
          ? 'bg-red-500/[0.08] border-red-500/20' 
          : 'bg-white/[0.02] border-white/10'
      }`}>
        <div className="flex items-start gap-3">
          <Wrench className={`w-5 h-5 flex-shrink-0 mt-0.5 ${maintenance.status === 'overdue' ? 'text-red-400' : 'text-sky-400'}`} />
          <div>
            <p className={`text-sm font-medium font-['Outfit'] ${maintenance.status === 'overdue' ? 'text-red-400' : 'text-white'}`}>
              Próximo mantenimiento
            </p>
            <p className={`text-xs mt-1 font-['Manrope'] ${maintenance.status === 'overdue' ? 'text-red-300' : 'text-zinc-400'}`}>
              {maintenance.text}
              {maintenance.date && ` (${maintenance.date})`}
            </p>
            {maintenance.status === 'overdue' && (
              <Link to="/maintenance">
                <Button size="sm" className="mt-3 bg-red-500 hover:bg-red-600 text-white text-xs">
                  Solicitar revisión
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Features */}
      {equipment.features && equipment.features.length > 0 && (
        <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
          <p className="text-xs uppercase tracking-wider text-zinc-500 mb-3 font-['Manrope']">Características</p>
          <div className="flex flex-wrap gap-2">
            {equipment.features.map((feature) => (
              <span key={feature} className="px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-300 text-xs font-['Manrope']">
                {feature}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Full equipment catalog data
const EQUIPMENT_CATALOG = [
  {
    name: 'KAJAAL',
    features: ['Agua del tiempo'],
    capacity: null,
    description: 'Compacta, elegante y discreta. Agua pura ilimitada con la máxima simplicidad.',
    price: '29,90€/mes',
    image: '/equipment-kajaal.png'
  },
  {
    name: 'TIKAL',
    features: ['Agua fría', 'Agua caliente', 'Agua del tiempo'],
    capacity: '3,5L agua fría',
    description: 'El más popular. Ideal para hogares y pequeñas oficinas.',
    price: '34,90€/mes',
    image: '/equipment-tikal.png'
  },
  {
    name: 'TIKAL S',
    features: ['Agua fría', 'Agua caliente', 'Agua del tiempo'],
    capacity: '1,7L agua fría',
    description: 'Versión compacta. Ideal para espacios reducidos.',
    price: '34,90€/mes',
    image: '/equipment-tikal-s.png'
  },
  {
    name: 'CHAAK',
    features: ['Agua fría', 'Agua caliente', 'Agua del tiempo'],
    capacity: '8L agua fría',
    description: 'Máxima capacidad. Ideal para empresas y familias grandes.',
    price: '44,90€/mes',
    image: '/equipment-chaak.png'
  }
];

function CatalogCard({ model, isCurrentEquipment = false }) {
  return (
    <div className={`p-4 rounded-xl border transition-all ${
      isCurrentEquipment 
        ? 'bg-sky-500/[0.08] border-sky-500/30 ring-2 ring-sky-500/20' 
        : 'bg-white/[0.02] border-white/10 hover:border-white/20'
    }`}>
      <div className="flex items-start gap-4">
        <img 
          src={model.image} 
          alt={model.name} 
          className="w-20 h-20 object-contain"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-medium text-white font-['Outfit']">{model.name}</h4>
            {isCurrentEquipment && (
              <span className="px-2 py-0.5 rounded-full bg-sky-500/20 text-[10px] text-sky-300 font-medium">
                Tu equipo
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 font-['Manrope'] mb-2 line-clamp-2">{model.description}</p>
          <div className="flex flex-wrap gap-1 mb-2">
            {model.features.map((f) => (
              <span key={f} className="px-2 py-0.5 rounded bg-white/5 text-[10px] text-zinc-400">
                {f}
              </span>
            ))}
          </div>
          {model.capacity && (
            <p className="text-xs text-sky-400 font-['Manrope']">{model.capacity}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function EquipmentPage() {
  const [equipments, setEquipments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await EquipmentService.getEquipments();
        setEquipments(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  if (loading) return <PortalLoading />;

  const equipment = equipments[0]; // Primary equipment

  return (
    <div className="min-h-screen bg-[#030303]">
      <PortalHeader />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-light text-white font-['Outfit']">Mis Equipos</h1>
          <p className="text-sm text-zinc-500 mt-1 font-['Manrope']">Gestiona tu equipo Manklar</p>
        </div>

        {!equipment ? (
          <div className="text-center py-12 rounded-2xl bg-white/[0.02] border border-white/10">
            <Droplets className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 font-['Manrope']">No tienes equipos instalados</p>
            <a href={WHATSAPP_MANKLAR_URL} target="_blank" rel="noopener noreferrer">
              <Button className="mt-4 bg-white text-black hover:bg-zinc-200">
                Solicitar equipo
              </Button>
            </a>
          </div>
        ) : (
          <>
            {/* Hero Card with Real Image */}
            <EquipmentHero equipment={equipment} />

            {/* Equipment Details */}
            <EquipmentDetails equipment={equipment} />

            {/* Warranty Info */}
            <div className="mt-6 p-4 rounded-xl bg-emerald-500/[0.08] border border-emerald-500/20">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-emerald-400 font-['Outfit']">Garantía Manklar</p>
                  <p className="text-xs text-zinc-400 mt-1 font-['Manrope']">
                    Tu equipo está protegido. Si comunicamos una avería y no queda resuelta en 48h laborables, 
                    abonamos la siguiente mensualidad.
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <Link to="/maintenance" className="p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors text-center">
                <Wrench className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                <p className="text-sm text-white font-['Manrope']">Solicitar revisión</p>
              </Link>
              <Link to="/help" className="p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors text-center">
                <Info className="w-6 h-6 text-zinc-400 mx-auto mb-2" />
                <p className="text-sm text-white font-['Manrope']">Centro de ayuda</p>
              </Link>
            </div>

            {/* Other Manklar Equipment Catalog */}
            <div className="mt-10">
              <h2 className="text-xs uppercase tracking-wider text-zinc-500 mb-4 font-['Manrope']">
                Otros equipos Manklar
              </h2>
              <p className="text-sm text-zinc-400 mb-6 font-['Manrope']">
                Conoce toda nuestra gama de dispensadores de agua purificada
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {EQUIPMENT_CATALOG.map((model) => {
                  const isCurrentEquipment = equipment.model?.toUpperCase().includes(model.name.toUpperCase()) ||
                                             equipment.name?.toUpperCase().includes(model.name.toUpperCase());
                  return (
                    <CatalogCard 
                      key={model.name} 
                      model={model} 
                      isCurrentEquipment={isCurrentEquipment}
                    />
                  );
                })}
              </div>
            </div>
          </>
        )}
      </main>

      <ScrollToTop />
    </div>
  );
}
